import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Department, IScheduleRotation} from '@signal/asp-data-commons';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Observable} from 'rxjs';
import {SubscriptionList} from '../../models/asp.types';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {TeamState} from '../../../store/team/team.reducer';
import {ScheduleRotationState} from '../../../store/schedule-rotation/schedule-rotation.reducer';
import { MatDialog } from '@angular/material/dialog';
import {extractDateAsString, unsubscribeSubscriptions} from '../../services/util.service';
import * as fromActions from '../../../store/schedule-rotation/schedule-rotation.actions';
import {AddItemComponent} from '../add-item/add-item.component';
import * as fromSelectors from '../../../store/schedule-rotation/schedule-rotation.selectors';
import {TranslateService} from '@ngx-translate/core';
import { DateAdapter } from '@angular/material/core';
import { AlertService } from '../../../shared/services/alert.service';
import { DateTime } from 'luxon';

@Component({
  selector: 'app-schedule-rotation-edit',
  templateUrl: './schedule-rotation-edit.component.html',
  styleUrls: ['./schedule-rotation-edit.component.scss']
})
export class ScheduleRotationEditComponent implements OnInit, OnChanges, OnDestroy {

  @Input() department = Department.SERVICE;
  @Input() scheduleId: string;
  @Input() saveClicked: boolean;
  @Output() formsEdited:EventEmitter<boolean>=new EventEmitter();

  subHeading: string;
  emptyText1: string;
  emptyText2: string;
  scheduleData: IScheduleRotation;
  dateForm: UntypedFormGroup;
  config: { id: string; department: Department};
  subs: SubscriptionList = {};
  dates: string[] = [];
  dateFilter: any;
  shiftIds$: Observable<string[]>;

  constructor(private readonly router: Router,
              private readonly activatedRoute: ActivatedRoute,
              private readonly teamState: Store<TeamState>,
              private readonly scheduleRotationState: Store<ScheduleRotationState>,
              public readonly dialog: MatDialog,
              public readonly formBuilder: UntypedFormBuilder,
              private readonly translate:TranslateService,
              private readonly alertService: AlertService,
              private dateAdapter: DateAdapter<Date>) {
              this.dateAdapter.setLocale(this.translate.currentLang);
  }

  ngOnInit() {
    this.initiateForm();
    this.setupStore();
    if (this.scheduleData?.department === Department.SALES) {
      this.subHeading = this.translate.instant('otherTexts.salesAdvisorsPool');
      this.emptyText1 = this.translate.instant('otherTexts.thereAreNoAdvisors');
      this.emptyText2 = this.translate.instant('otherTexts.dragServiceAdvisorsFromTheSidebar');
    } else {
      this.subHeading = this.translate.instant('scheduleRotation.teams'); 
      this.emptyText1 = this.translate.instant('otherTexts.thereAreNoTeams');
      this.emptyText2 = this.translate.instant('otherTexts.dragTeamsFromTheSidebar');
    }
    this.subs.dateForm = this.dateForm.valueChanges.subscribe(e=>{
      this.formsEdited.emit(false);
    })
  }

  initiateForm() {
    this.dateForm = this.formBuilder.group({
      startDate: [this.scheduleData?.startDate ? DateTime.fromFormat(this.scheduleData?.startDate, 'yyyy-MM-dd').toISO() : '', Validators.required]
    });
  }

  addShift() {
    const dialogRef = this.dialog.open(AddItemComponent, {
      width: '25rem',
      height: '483px',
      panelClass: 'asp-modal-container',
      data: {isEditMode: false, title: 'scheduleRotation.addNewShift', type: 'shift',clicked:true},
      disableClose:true
    });
    this.subs.dialog = dialogRef.afterClosed().subscribe(event => {
      if (event.action === 'confirm') {
        if(this.scheduleData && this.scheduleData.shifts){
          // Generated by Copilot- Start
          const existingShift = this.scheduleData.shifts.find(s => s.name.toUpperCase() === event.data.name.toUpperCase());
          if(existingShift){
            this.alertService.showAlert(`otherTexts.cannotCreateShift`);
            return 
          }
          // Generated by Copilot- End
        }
        this.scheduleRotationState.dispatch(new fromActions.AddShift({
          department: this.department,
          id: this.scheduleData?.id,
          shift: event.data
        }));
      }
    });
    this.formsEdited.emit(false);
  }

  onSave() {
    const startDateForm = this.dateForm.value.startDate;
    const extractedStartDate = startDateForm ? extractDateAsString(startDateForm) : '';
    const parsedStartDate = extractedStartDate ? DateTime.fromISO(extractedStartDate) : undefined;
    const startDate = parsedStartDate?.isValid ? parsedStartDate.toFormat('yyyy-MM-dd') : '';
    if(startDate === this.scheduleData?.startDate){
      this.scheduleRotationState.dispatch(new fromActions.UpdateStartDate({
        id: this.scheduleData?.id,
        department: this.department,
        startDate
      }));
    }
    this.scheduleRotationState.dispatch(new fromActions.SaveScheduleRotation({
      department: this.department,
      scheduleRotation: {...this.scheduleData, startDate}
    }));
    this.dateForm.markAsPristine();
    this.formsEdited.emit(true);
  }

  // Generated by Copilot- Start
  getShiftById(shiftId: string) {
    return this.scheduleData?.shifts?.find(s => s.id === shiftId);
  }
  // Generated by Copilot- End

  cancel() {
    this.router.navigate([`asp/${this.department.toLowerCase()}/schedulerotation`]);
  }

  ngOnDestroy(): void {
    unsubscribeSubscriptions(this.subs);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('shiftId') || changes.hasOwnProperty('department')) {
      this.setupStore();
    }
    if(this.saveClicked)
    {
      this.onSave();
    }
  }

  private setupStore() {
    /* Select sorted shift ids */
    this.shiftIds$ = this.scheduleRotationState.select(fromSelectors.selectScheduleRotationShiftsSortedId, {
      department: this.department, id: this.scheduleId
    });

    /* select and subscribe the schedule rotation data */
    if (this.subs.scheduleRotationSub) {
      this.subs.scheduleRotationSub.unsubscribe();
    }
    this.subs.scheduleRotationSub = this.scheduleRotationState
      .select(fromSelectors.selectScheduleRotationDataById,
        {department: this.department, id: this.scheduleId})
      .subscribe(data => {
        /* assign to class variable */
        this.scheduleData = data;

        /* reselect the all other dates */
        if (this.subs.allDatesSub) {
          this.subs.allDatesSub.unsubscribe();
        }
        this.subs.allDatesSub = this.scheduleRotationState.select(fromSelectors.selectAllOtherStartDates, {
          department: this.department,
          id: this.scheduleData?.id
        })
          .subscribe(dates => {
            this.dates = dates;
            this.dateFilter = (date: Date | null): boolean => {
              // if(!date) {
              //   return !date;
              // }
              const day = (date || new Date());
              const dateFormatted = extractDateAsString(day);
              return !this.dates.includes(dateFormatted);
            };
          });

        /* set form date value */
        this.config = {department: this.department, id: this.scheduleData?.id};
      });
  }
  nextClicked(){
    this.dateAdapter.setLocale('es');
  }
}
